/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
// import { Chart as ChartJS, registerables } from 'chart.js';
// import { Chart } from 'react-chartjs-2'
// ChartJS.register(...registerables);
// import 'chartjs-adapter-moment';

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Soft UI Dashboard PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

//Backend access
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getLogs, loadToken } from 'api/bks';

import { Route, Link, Routes, useLocation, useNavigate, useMatch } from 'react-router-dom';
import moment from "moment";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";

import GradientMinMaxLineChart from "nextus/Charts/GradientMinMaxLineChart/timeline"

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { getAggregatedCameraObjects } from "api/bks";



function CrowdCountingChart({ chartData, startLimit, endLimit, startTime, setStartTime, endTime, setEndTime, groupSize, setGroupSize, height = "19.125rem", maxY = undefined, setCurrentTime, event_data, shownEpoch }) {
    // console.log(groupSize)
    loadToken(useAuth0());

    const chartRef = useRef(null);
    const [min_value, setMinChartValue] = useState({});
    const [max_value, setMaxChartValue] = useState({});
    const [loading, setLoading] = useState(true);


    var applyGradient = (context) => {
        if (!context.chart.chartArea) {
            return;
        }
        const { ctx, chartArea: { top, bottom } } = context.chart;
        var gradientBg = ctx.createLinearGradient(0, top, 0, bottom);


        // gradientBg.addColorStop(4 / 5, 'rgba(33,102,172, 0)');
        gradientBg.addColorStop(4 / 5, 'rgba(38, 124, 204, 0.8)');
        gradientBg.addColorStop(3 / 5, 'rgba(131, 180, 226, 0.8)');
        gradientBg.addColorStop(2 / 5, 'rgba(239,128,98, 0.8)');
        gradientBg.addColorStop(0 / 5, 'rgba(255, 24, 24, 0.8)');
        
        return gradientBg;
    }


    function processChartData(data) {
        var datasets = {
            "min": [],
            "max": [],
            "mean": []
        };

        for (var value in data) {

            var gap = (data[value].start) / data[value]["_id"];

            var timeslot = (data[value].start) + (gap / 2);

            if (timeslot > moment().unix()) {
                timeslot = moment().unix();
            }

            for (var key in datasets) {
                datasets[key].push({
                    'y': data[value][key],
                    'x': timeslot * 1000
                });
            }
        }

        // console.log(datasets);

        var max = Math.max(...datasets.max.map(value => value.y));
        var min = Math.min(...datasets.min.map(value => value.y));

        const chart_data = {
            labels: [],
            datasets: [
                {
                    label: "Lower Range",
                    backgroundColor: applyGradient,
                    borderColor: applyGradient,
                    data: datasets.min,
                    fill: 1,
                    type: 'line',
                    radius: 0,
                    order: 2,
                    tension: 0.3,
                    categoryPercentage: 1.0,
                    barPercentage: 1.0,
                    borderSkipped: false
                },
                {
                    label: "Upper Range",
                    backgroundColor: applyGradient,
                    borderColor: applyGradient,
                    data: datasets.max,
                    fill: 1,
                    type: 'line',
                    radius: 0,
                    order: 2,
                    tension: 0.3,
                    categoryPercentage: 1.0,
                    barPercentage: 1.0,
                    borderSkipped: false

                },
                {
                    label: 'Average',
                    data: datasets.mean,
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    borderColor: "rgba(0, 0, 0, 0.4)",
                    type: 'line',
                    order: 1,
                    tension: 0.3,
                    pointRadius: 3,
                    pointHoverRadius: 10,
                }
            ]
        };

        return {
            chart_data, min, max
        }
    }

    // console.log(chartData)

    const { chart_data, min, max } = processChartData(chartData)
    // console.log(shownEpoch)

    return (<SoftBox sx={{ height }}>
        {chart_data && <GradientMinMaxLineChart chartData={chart_data} event_data={event_data} startLimit={startLimit} endLimit={endLimit} min_value={min} max={maxY ? maxY : max} startTime={startTime * 1000} endTime={endTime * 1000} groupSize={groupSize} setStartTime={setStartTime} setEndTime={setEndTime} setCurrentTime={setCurrentTime} shownEpoch={shownEpoch} />}
    </SoftBox>);

}

// Typechecking props for the CrowdCountingChart
CrowdCountingChart.propTypes = {
    chartData: PropTypes.array,
    startLimit: PropTypes.number,
    endLimit: PropTypes.number,
    startTime: PropTypes.object,
    setStartTime: PropTypes.func,
    endTime: PropTypes.object,
    setEndTime: PropTypes.func,
    groupSize: PropTypes.number,
    maxY: PropTypes.number,
    setGroupSize: PropTypes.func,
    height: PropTypes.number,
    currentTime: PropTypes.object,
    setCurrentTime: PropTypes.func,
    event_data: PropTypes.array
};

export default CrowdCountingChart;

